import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Sparkles } from "lucide-react";
import api from "@/lib/api";
import { playSay } from "@/lib/readingAudio";
import ExerciseRunner from "@/components/reading/ExerciseRunner";
import { ReadingBg, Btn } from "@/pages/ReadingHome";

// Stop the test as soon as a part has 3 mistakes (that part decides the starting lesson).
const diagStop = (ex, errors) => (errors[ex.part] || 0) >= 3;

export default function ReadingDiagnostic() {
  const { childId, lang } = useParams();
  const navigate = useNavigate();
  const [qs, setQs] = useState(null);
  const [phase, setPhase] = useState("intro");
  const [result, setResult] = useState(null);
  const [style, setStyle] = useState("petit");
  const fr = lang === "fr";
  useEffect(() => {
    api.get(`/reading/${childId}/${lang}/diagnostic`).then(({ data }) => setQs(data.questions)).catch(() => navigate(`/kid/${childId}/reading`));
    api.get(`/reading/${childId}/home`).then(({ data }) => setStyle(data.style)).catch(() => {});
  }, [childId, lang, navigate]);
  const intro = fr ? "Petit test pour trouver ton niveau. Fais de ton mieux, ce n'est pas grave si c'est difficile !"
    : "A little test to find your level. Just do your best, it's fine if it gets hard!";
  const finish = async (res) => {
    const { data } = await api.post(`/reading/${childId}/${lang}/diagnostic`, { answers: res.answers });
    setResult(data);
    setPhase("done");
    playSay(fr ? `Bravo ! Tu commences par : ${data.start_title}` : `Well done! You will start with: ${data.start_title}`, lang);
  };
  return (
    <ReadingBg petit={style === "petit"}>
      <header className="max-w-4xl mx-auto px-5 pt-5 flex items-center gap-3">
        <button onClick={() => navigate(`/kid/${childId}/reading`)} className="w-11 h-11 rounded-full bg-white border-[3px] border-slate-900 flex items-center justify-center" data-testid="diag-back"><ArrowLeft /></button>
        <h1 className="text-2xl sm:text-3xl font-bold">{fr ? "Le test de départ" : "Starting test"}</h1>
      </header>
      <main className="max-w-4xl mx-auto px-5 py-8 flex flex-col items-center">
        {phase === "intro" && (
          <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="flex flex-col items-center gap-6 text-center" data-testid="diag-intro">
            <motion.img src="/mascot/kiko.png" alt="Kiko" className="w-40 h-40" animate={{ rotate: [0, -8, 8, 0] }} transition={{ repeat: Infinity, duration: 2 }} />
            <p className="text-xl sm:text-2xl font-bold max-w-lg">{intro}</p>
            <Btn onClick={() => { playSay(intro, lang); setPhase("test"); }} testid="diag-start" color="bg-blue-600 text-white"><Sparkles /> {fr ? "C'est parti !" : "Let's go!"}</Btn>
          </motion.div>
        )}
        {phase === "test" && qs && <ExerciseRunner childId={childId} lang={lang} petit={style === "petit"} items={qs} mode="diag" diagStop={diagStop} onDone={finish} />}
        {phase === "done" && result && (
          <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="flex flex-col items-center gap-5 text-center" data-testid="diag-result">
            <div className="text-6xl">🏆</div>
            <h2 className="text-3xl font-bold">{fr ? "Test terminé !" : "Test finished!"}</h2>
            <p className="text-xl font-semibold" data-testid="diag-start-lesson">{fr ? "Tu commences par :" : "You start with:"} <b>{result.start_title}</b></p>
            {result.note === "above_pilot" && <p className="text-sm font-semibold text-slate-600 max-w-md">{fr ? "Tu lis déjà très bien ! On révise la dernière leçon, et tes parents seront prévenus." : "You already read very well! We'll review the last lesson and let your parent know."}</p>}
            <Btn onClick={() => navigate(`/kid/${childId}/reading/${lang}`)} testid="diag-go-path" color="bg-green-500 text-white">{fr ? "Voir mon parcours" : "See my path"}</Btn>
          </motion.div>
        )}
      </main>
    </ReadingBg>
  );
}
